import { InformationCircleIcon } from "@heroicons/react/24/outline";
import SelectComponent from "@via/components/select/select.tsx";
import {
  AwsAuthMode,
  isStaticKeyModeBlocked,
} from "@via/pages/account/profile/run-environments/run-environment-form/aws-auth-mode.ts";
import { MembershipConnectionsWarning } from "@via/pages/organization/membership-connections.tsx";
import { ExecutorJobsType, type ProfileClusterForClient } from "common";
import { type JSX } from "react";
import { type UseFormRegister } from "react-hook-form";
import { Tooltip } from "react-tooltip";

export const cloudCredentialsCardSummary = (params: {
  selectedExecutorJobs: ExecutorJobsType;
  awsAuthMode: AwsAuthMode;
  credentialLabel?: string;
  region?: string | null;
}): string => {
  // This card can materialize already collapsed, so the summary is all a user
  // sees of it. A bare mode name reads as a setup that is already complete, so
  // an unresolved half says so rather than staying silent about it.
  if (params.selectedExecutorJobs === ExecutorJobsType.GoogleBatch) {
    return params.region?.trim()
      ? `Google Batch, ${params.region.trim()}`
      : "Google Batch, no region";
  }
  // Instance role attaches no credential at all, and a row that carries both
  // the flag and a saved id still derives this mode, so return before any
  // label can be appended to it. It is the one mode with nothing missing.
  if (params.awsAuthMode === AwsAuthMode.InstanceRole) return "instance role";
  const mode =
    params.awsAuthMode === AwsAuthMode.AssumeRole
      ? "assume role"
      : "static key";
  return params.credentialLabel
    ? `${mode}, ${params.credentialLabel}`
    : `${mode}, no credential`;
};

export function CloudCredentialsCard({
  register,
  selectedExecutorJobs,
  awsAuthMode,
  onAwsAuthModeChange,
  requireRoleBasedAwsAuth,
  canUseAssumeRole,
  amazonCredentials,
  assumeRoleCredentialOptions,
  failedOrganizationNames = [],
  selectedAmazonId,
  onAwsIdChange,
  googleCredentials,
  selectedGoogleId,
  onGoogleIdChange,
  isSavedCredentialsLoading,
}: {
  register: UseFormRegister<ProfileClusterForClient>;
  selectedExecutorJobs: ExecutorJobsType;
  awsAuthMode: AwsAuthMode;
  onAwsAuthModeChange: (mode: AwsAuthMode) => void;
  requireRoleBasedAwsAuth: boolean;
  // Any active membership, wherever it sorts (#1135).
  canUseAssumeRole: boolean;
  // The saved-credentials API types name as optional, so the label is too.
  amazonCredentials: { label: string | undefined; value: number }[];
  assumeRoleCredentialOptions: { label: string; value: number }[];
  // Organizations whose assume role list could not be loaded.
  failedOrganizationNames?: string[];
  selectedAmazonId?: number;
  onAwsIdChange: (option: any) => void;
  googleCredentials: { label: string | undefined; value: number }[];
  selectedGoogleId?: number;
  onGoogleIdChange: (option: any) => void;
  isSavedCredentialsLoading: boolean;
}): JSX.Element {
  return (
    <>
      {selectedExecutorJobs === ExecutorJobsType.AwsBatch && (
        <div data-testid="aws-auth-mode">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            AWS Authentication
          </label>
          <div className="mt-2 flex flex-col gap-2">
            {!requireRoleBasedAwsAuth && (
              <label className="flex items-center gap-2 text-sm text-gray-900">
                <input
                  type="radio"
                  name="awsAuthMode"
                  checked={awsAuthMode === AwsAuthMode.StaticKey}
                  onChange={() => onAwsAuthModeChange(AwsAuthMode.StaticKey)}
                />
                Static key
              </label>
            )}
            {canUseAssumeRole ? (
              <label className="flex items-center gap-2 text-sm text-gray-900">
                <input
                  type="radio"
                  name="awsAuthMode"
                  checked={awsAuthMode === AwsAuthMode.AssumeRole}
                  onChange={() => onAwsAuthModeChange(AwsAuthMode.AssumeRole)}
                />
                Assume role
              </label>
            ) : null}
            <label className="flex items-center gap-2 text-sm text-gray-900">
              <input
                type="radio"
                name="awsAuthMode"
                checked={awsAuthMode === AwsAuthMode.InstanceRole}
                onChange={() => onAwsAuthModeChange(AwsAuthMode.InstanceRole)}
              />
              Attached instance role
            </label>
          </div>

          {awsAuthMode === AwsAuthMode.StaticKey &&
            !requireRoleBasedAwsAuth && (
              <div className="mt-3" data-testid="amazon-id">
                <SelectComponent
                  name="amazonId"
                  onChange={onAwsIdChange}
                  value={amazonCredentials.find(
                    aws => aws.value === selectedAmazonId
                  )}
                  options={amazonCredentials}
                  isLoading={isSavedCredentialsLoading}
                  enableClear={true}
                  placeholder={"Select Amazon Credentials"}
                />
              </div>
            )}

          {isStaticKeyModeBlocked(awsAuthMode, requireRoleBasedAwsAuth) && (
            <p
              className="mt-3 text-xs text-rose-500"
              data-testid="static-key-blocked">
              Your organization requires role-based AWS authentication. Choose
              assume role or attached instance role.
            </p>
          )}

          {awsAuthMode === AwsAuthMode.AssumeRole && (
            <div className="mt-3" data-testid="assume-role-id">
              <SelectComponent
                name="assumeRoleId"
                onChange={onAwsIdChange}
                value={assumeRoleCredentialOptions.find(
                  c => c.value === selectedAmazonId
                )}
                options={assumeRoleCredentialOptions}
                enableClear={true}
                placeholder={"Select an assume-role credential"}
              />
              {assumeRoleCredentialOptions.length === 0 ? (
                <p className="mt-1 text-xs text-gray-500">
                  No assume-role credentials yet. An org admin can create one in
                  Organization Settings.
                </p>
              ) : null}
              <MembershipConnectionsWarning names={failedOrganizationNames} />
            </div>
          )}

          {awsAuthMode === AwsAuthMode.InstanceRole && (
            <p className="mt-3 text-xs text-gray-500">
              The run uses the compute&apos;s attached IAM role. Only the region
              is applied, no keys are stored or injected.
            </p>
          )}
        </div>
      )}

      {selectedExecutorJobs === ExecutorJobsType.GoogleBatch && (
        <div>
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Google Cloud Credentials
            </label>
            <div className="mt-2" data-testid="google-id">
              <SelectComponent
                name="googleId"
                onChange={onGoogleIdChange}
                value={googleCredentials.find(
                  google => google.value === selectedGoogleId
                )}
                options={googleCredentials}
                isLoading={isSavedCredentialsLoading}
                enableClear={true}
                placeholder={"Select Google Cloud Credentials"}
              />
            </div>
          </div>

          <div>
            <label className="block mt-2 flex items-center gap-2 text-sm font-medium leading-6 text-gray-900">
              Region
              <InformationCircleIcon id="tooltip-region" className="h-5 w-5" />
            </label>
            <Tooltip
              anchorSelect="#tooltip-region"
              content="Make sure to specify a location where Google Batch is available. Refer to the Google Batch documentation for region availability."
            />
            <div className="mt-2">
              <input
                {...register("region")}
                type="text"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder="Enter Region"
              />
            </div>
          </div>

          <div>
            <label className="block mt-2 flex items-center gap-2 text-sm font-medium leading-6 text-gray-900">
              Project Id
              <InformationCircleIcon
                id="tooltip-project-id"
                className="h-5 w-5"
              />
            </label>
            <Tooltip
              className={"max-w-96"}
              anchorSelect="#tooltip-project-id"
              content={`If you already selected Google Key, you can leave this section empty. Otherwise, please get Project ID by following these steps: Open the Google Cloud Console. On the dashboard, find 'Project Info' box and copy 'Project ID' field. eg.Foundry-193616"`}
            />
            <div className="mt-2">
              <input
                {...register("projectId")}
                type="text"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder="Enter Project Id"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Spot Instance
            </label>
            <div>
              <input
                {...register("spotInstance")}
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
